import React, { useState, useEffect } from 'react';
import {
  Grid, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, CircularProgress, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ACTIONS } from '../../../../shared/constants';
import { getUser } from '../../../../shared/auth-service';
import { getallemployeesWithZones } from '../logic';

const ManagementZoneBulkAssign = ({
  handleClose,
  handleSave,
  airports,
  zones,
}) => {
  const userData = getUser();
  const companyId = userData.countryCode.split('-')[0];

  const [selectedBase, setSelectedBase] = useState(userData.airport?.id || 0);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedZoneValue, setSelectedZoneValue] = useState(null);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingEmployees(true);
      const data = await getallemployeesWithZones(selectedBase, companyId);
      setEmployees(data || []);
      setLoadingEmployees(false);
    };
    load();
  }, [selectedBase]);

  const handleAddZone = (zoneObj) => {
    if (!zoneObj || selectedZones.some(z => z.id === zoneObj.id)) return;
    setSelectedZones(prev => [...prev, { id: zoneObj.id, description: zoneObj.description }]);
    setIsDirty(true);
    setAccordionOpen(true);
  };

  const onSave = () => {
    if (selectedEmployees.length === 0 || selectedZones.length === 0) return;
    handleSave({
      employeeIds: selectedEmployees.map(e => e.employeeId),
      zones: selectedZones.map(z => ({ Id: z.id, Description: z.description }))
    });
  };

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupAddIcon sx={{ color: '#00b0ef' }} />
            <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
              Asignación de <span style={{ color: '#00b0ef' }}>Zonas</span>
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Empleados */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Empleados
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Base</InputLabel>
                  <Select
                    label="Base"
                    value={selectedBase}
                    onChange={(e) => { setSelectedBase(e.target.value); setSelectedEmployees([]); }}
                  >
                    <MenuItem value={0}>Todas</MenuItem>
                    {airports.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.code}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={9}>
                {loadingEmployees ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Cargando empleados...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    multiple
                    size="small"
                    options={employees}
                    getOptionLabel={(o) => `${o.employeeId} - ${o.name} ${o.lastName}`}
                    value={selectedEmployees}
                    onChange={(_, newValue) => { setSelectedEmployees(newValue); setIsDirty(true); }}
                    isOptionEqualToValue={(o, v) => o.employeeId === v.employeeId}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.employeeId}
                          label={`${option.employeeId} - ${option.name} ${option.lastName}`}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{ bgcolor: '#e8f0fe', color: '#1a3072' }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Empleados seleccionados (${selectedEmployees.length})`}
                        placeholder={selectedEmployees.length === 0 ? 'Buscar y seleccionar...' : ''}
                      />
                    )}
                    noOptionsText="Sin resultados"
                  />
                )}
              </Grid>
              {selectedEmployees.length === 1 && selectedEmployees[0]?.employeePosition?.name && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Cargo:</Typography>
                    <Typography variant="body2" sx={{ color: '#1a3072', fontWeight: 500 }}>
                      {selectedEmployees[0].employeePosition.name}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Zonas */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Asignación de Zonas
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              options={[{ id: -1, description: 'Todas' }, ...zones]}
              getOptionLabel={(o) => o.description || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedZoneValue}
              onChange={(_, newValue) => {
                setSelectedZoneValue(newValue);
                if (!newValue) return;
                if (newValue.id === -1) {
                  setSelectedZones(zones.map(z => ({ id: z.id, description: z.description })));
                  setIsDirty(true);
                  setAccordionOpen(true);
                  setSelectedZoneValue(null);
                } else {
                  handleAddZone(newValue);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Zona" variant="outlined" size="small" />}
              noOptionsText="Sin resultados"
            />
          </FormControl>

          {selectedZones.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
              <Typography variant="body2">Sin zonas seleccionadas</Typography>
            </Box>
          ) : (
            <Accordion expanded={accordionOpen} disableGutters elevation={0}
              onChange={(_, isExpanded) => setAccordionOpen(isExpanded)}
              sx={{ border: '1px solid #e0e4ed', borderRadius: '8px !important', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: '#f0f4ff', borderRadius: '8px', minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      Zonas
                    </Typography>
                    <Chip label={selectedZones.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1a3072', color: '#fff' }} />
                  </Box>
                  <Chip
                    icon={<DeleteSweepIcon sx={{ fontSize: '14px !important' }} />}
                    label="Quitar todo"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setSelectedZones([]); setIsDirty(true); setAccordionOpen(false); }}
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#e53935', border: '1px solid #fecaca', '& .MuiChip-icon': { color: '#e53935' }, '&:hover': { bgcolor: '#fee2e2' } }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedZones.map(z => (
                    <Chip
                      key={z.id}
                      label={z.description}
                      size="small"
                      onDelete={() => { setSelectedZones(prev => prev.filter(el => el.id !== z.id)); setIsDirty(true); }}
                      sx={{ bgcolor: '#e8f0fe', color: '#1a3072', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {selectedEmployees.length > 0 && selectedZones.length > 0 && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Se asignarán <strong>{selectedZones.length} zona(s)</strong> a <strong>{selectedEmployees.length} empleado(s)</strong>.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ m: 1 }}>
          <Button color="primary" variant="contained" onClick={onSave} endIcon={<SaveIcon />}
            disabled={selectedEmployees.length === 0 || selectedZones.length === 0}>
            {ACTIONS.SAVE}
          </Button>
          <Box sx={{ m: 1 }}>
            <Button endIcon={<CancelIcon />} variant="outlined"
              onClick={() => isDirty ? setShowCancelAlert(true) : handleClose()}>
              {ACTIONS.CANCEL}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>¿Descartar cambios?</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="outlined" onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}>
            Seguir editando
          </Button>
          <Button variant="contained" onClick={() => { setShowCancelAlert(false); handleClose(); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}>
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementZoneBulkAssign;
